import React from 'react';
import { ChevronRight } from 'lucide-react';
import { PAStatus } from '../../../../dtos/patientmr/authorizations.dto';

interface GuideBannerProps {
  status: PAStatus | string;
  expirationAlertLevel?: 'green' | 'yellow' | 'orange' | 'red' | 'expired' | 'none';
  expirationDaysRemaining?: number | null;
}

interface BannerConfig {
  classes: string;
  title: string;
  steps: string[];
}

const BANNER_MAP: Record<string, BannerConfig> = {
  [PAStatus.DRAFT]: {
    classes: 'bg-slate-50 border-slate-200 text-slate-700',
    title: 'Next steps',
    steps: [
      'Fill out request details below',
      'Select related CCP PA form from Forms module',
      'Medicaid PDN: link a finalized F00120 flow sheet',
      "Generate the packet, or download PA forms from your payer's portal",
      'Complete and get required signatures',
      'Upload signed forms below',
      'Mark as Submitted'
    ]
  },
  [PAStatus.SUBMITTED]: {
    classes: 'bg-blue-50 border-blue-200 text-blue-700',
    title: 'Awaiting decision',
    steps: [
      'Check payer portal for updates',
      'Record decision using buttons above when received'
    ]
  },
  [PAStatus.PENDED]: {
    classes: 'bg-amber-50 border-amber-200 text-amber-700',
    title: 'Action needed',
    steps: [
      'Payer requested more information',
      'Upload additional docs below and resubmit'
    ]
  },
  [PAStatus.EXPIRED]: {
    classes: 'bg-slate-50 border-slate-200 text-slate-600',
    title: 'Authorization expired',
    steps: [
      'Create new PA to continue services',
      'Use Copy to pre-fill from this request'
    ]
  },
  [PAStatus.EXHAUSTED]: {
    classes: 'bg-orange-50 border-orange-200 text-orange-700',
    title: 'All approved units used',
    steps: ['Create new PA for additional authorization']
  }
};

export const GuideBanner: React.FC<GuideBannerProps> = ({ status, expirationAlertLevel, expirationDaysRemaining }) => {
  let config = BANNER_MAP[status];

  // Special case: APPROVED with server-reported expiration warning
  if (
    status === PAStatus.APPROVED &&
    expirationAlertLevel != null &&
    (expirationAlertLevel === 'red' || expirationAlertLevel === 'orange' || expirationAlertLevel === 'yellow')
  ) {
    config = {
      classes: 'bg-amber-50 border-amber-200 text-amber-700',
      title: `Expiring in ${expirationDaysRemaining ?? '?'} days`,
      steps: ['Start recertification PA now to avoid service gap']
    };
  }

  if (!config) return null;

  return (
    <div className={`mx-4 mt-4 p-3 border rounded-lg ${config.classes}`}>
      <p className="text-sm font-medium">{config.title}</p>
      <div className="mt-1.5 space-y-1">
        {config.steps.map((step, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs">
            <span className="opacity-40"><ChevronRight size={12} /></span>
            {step}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GuideBanner;
